import React, { useMemo, Fragment, useEffect, useState } from "react";
import {
  MaterialReactTable,
  MRT_FullScreenToggleButton as MRTFullScreenToggleButton,
  MRT_ShowHideColumnsButton as MRTShowHideColumnsButton,
  MRT_ToggleFiltersButton as MRTToggleFiltersButton,
} from "material-react-table";
import Typography from "@mui/material/Typography";

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
// import format from 'date-fns/format';
import {
  useDeleteReportResourceByIdMutation,
  useViewAllAdminReportResourceListMutation,
} from "../../../../reactQueries/AppApi/rqHooksForClientSpecificReports";
import { dateformatConverter } from "../../../../common/utilities/dates/dateformatConverter";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import { clientSpecificReportCreatePath, clientSpecificReportEditPath } from "../../../../routes/routePaths";
import ReplayIcon from '@mui/icons-material/Replay';
import Tooltip from "@mui/material/Tooltip";
import MpgConfirmDialog from "../../../../common/dialog/MpgConfirmDialog";
import MpgErrorDialog from "../../../../common/dialog/MpgErrorDialog";
import NoTableDataComp from "../../../../common/otherCommonComponents/NoTableDataComp";
import HtmlTooltip from "../../../../common/tooltips/HtmlTooltip";
import { useGetLoggedInUserDetails } from "../../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import useTrackUserActivity from "../../../../userActivityTracking/useTrackUserActivity";
import ExportButton from "../../../../common/otherCommonComponents/ExportButton";
import { useTrackActivityMutation } from "../../../../reactQueries/commonRQHooks";
import { getAccessTypes } from "../../../../hooksAndFunctions/functions/getAccessTypes";

const TooltipCell = ({ renderedCellValue }) => (
  <HtmlTooltip title={renderedCellValue}>{renderedCellValue}</HtmlTooltip>
);

const returnLabel = (key, labelArray = []) => {
  return labelArray.find((label) => label.Key === key)?.Phrase || '';
};

const ViewClientSpecificReport = () => {
  const { refClientSpecificCmsData } = useOutletContext();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const { clientPortalAdminRoleId, isEnterpriseAdmin, id: userId } = loggedInUser;
  const { mutate: trackActivityMutate } = useTrackActivityMutation();
  const { isEnterpriseAdmin: isEnterpriseAdminType, clientPortalAdminType } = getAccessTypes()
  const { hasA1Access } = clientPortalAdminType

  const canExportXls = isEnterpriseAdminType || hasA1Access;

  const { mutate: getAllReportList, isSuccess, data: reportsData, isError, isLoading: isLoadingOnReportData, } = useViewAllAdminReportResourceListMutation();

  useEffect(() => {
    getAllReportList({ loggedInUserId: userId, roleId: clientPortalAdminRoleId, isEnterpriseAdmin })
  }, [loggedInUser])

  const {
    mutate: deleteReportResourceById,
    isSuccess: isSuccessOnDelete,
    variables: variablesOnDelete
  } = useDeleteReportResourceByIdMutation();

  /**refeching on delete/recover */
  useEffect(() => {
    if (isSuccessOnDelete) {
      getAllReportList({ loggedInUserId: userId, roleId: clientPortalAdminRoleId, isEnterpriseAdmin })
    }
  }, [isSuccessOnDelete]);

  const fetchClientSpecificCMSLabel = (key) => {
    const labels = refClientSpecificCmsData?.Components?.ClientSpecificReportsList?.Labels;
    return returnLabel(key, labels);
  };
  const fetchCommonComponentCMSLabel = (key) => {
    const labels = refClientSpecificCmsData?.Components?.CommonComponent?.Labels;
    return returnLabel(key, labels);
  };

  const clientSpecificReportsTitle = refClientSpecificCmsData?.Components?.ClientSpecificReportsList ? fetchClientSpecificCMSLabel('ClientSpecificReportsList.ClientSpecificReportsTitle') : 'Client Specific Reports';
  const exportFileName = `${clientSpecificReportsTitle}.xlsx`;
  const exportWorksheetName = clientSpecificReportsTitle;

  const exportColumns = [
    { id: 'Title', header: fetchClientSpecificCMSLabel("ClientSpecificReportsList.Title"), getValue: (row) => row?.Title || '' },
    { id: 'ResourceType', header: fetchClientSpecificCMSLabel("ClientSpecificReportsList.Type"), getValue: (row) => row?.ResourceType || '' },
    { id: 'Scope', header: fetchClientSpecificCMSLabel("ClientSpecificReportsList.VisibleTo"), getValue: (row) => row?.Scope || '' },
    { id: 'ClientAccountName', header: fetchClientSpecificCMSLabel("ClientSpecificReportsList.ClientAccount"), getValue: (row) => row?.ClientAccountName || '' },
    { id: 'Status', header: fetchClientSpecificCMSLabel("ClientSpecificReportsList.Status"), getValue: (row) => row?.Status || '' },
    { id: 'CreatedBy', header: fetchClientSpecificCMSLabel("ClientSpecificReportsList.PublishedBy"), getValue: (row) => row?.CreatedBy || '' },
    { id: 'publishedOn', header: fetchClientSpecificCMSLabel("ClientSpecificReportsList.PublishedOn"), getValue: (row) => row?.CreatedTimeStamp ? dateformatConverter(row.CreatedTimeStamp, "DD MMM YYYY hh:mm A") : '' },
  ];

  const columns = useMemo(
    () => [
      {
        accessorKey: "Title",
        header: fetchClientSpecificCMSLabel("ClientSpecificReportsList.Title"),
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: { placeholder:fetchClientSpecificCMSLabel("ClientSpecificReportsList.PlaceholderTitle") }
      },
      {
        accessorKey: "ResourceType",
        header:fetchClientSpecificCMSLabel("ClientSpecificReportsList.Type"),
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: { placeholder:fetchClientSpecificCMSLabel("ClientSpecificReportsList.PlaceholderType") }
      },
      {
        accessorKey: "Scope",
        header: fetchClientSpecificCMSLabel("ClientSpecificReportsList.VisibleTo"),
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: { placeholder:fetchClientSpecificCMSLabel("ClientSpecificReportsList.PlaceholderVisibleTo") }
      },
      {
        accessorKey: "ClientAccountName",
        header: fetchClientSpecificCMSLabel("ClientSpecificReportsList.ClientAccount"),
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: { placeholder:fetchClientSpecificCMSLabel("ClientSpecificReportsList.PlaceholderClientAccount") }
      },
      {
        accessorKey: "Status",
        header: fetchClientSpecificCMSLabel("ClientSpecificReportsList.Status"),
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: { placeholder:fetchClientSpecificCMSLabel("ClientSpecificReportsList.PlaceholderStatus") }
      },
      {
        accessorKey: "CreatedBy",
        header: fetchClientSpecificCMSLabel("ClientSpecificReportsList.PublishedBy"),
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: { placeholder:fetchClientSpecificCMSLabel("ClientSpecificReportsList.PlaceholderPublishedBy") }
      },
      {
        accessorFn: (originalRow) => {
          const data = dateformatConverter(originalRow.CreatedTimeStamp, "DD MMM YYYY hh:mm A");
          return `${data}`
        },
        id: 'publishedOn',
        header: fetchClientSpecificCMSLabel("ClientSpecificReportsList.PublishedOn"),
        filterFn: 'contains',
        sortingFn: 'datetime',
        muiTableHeadCellFilterTextFieldProps: { placeholder:fetchClientSpecificCMSLabel("ClientSpecificReportsList.PlaceholderPublishedOn") }
      },
    ],
    [refClientSpecificCmsData]
  );

  /**redirect hook */
  let navigate = useNavigate();

  /**confirm dialog state on delete action */
  const [openMpgConfirmDialog, setOpenMpgConfirmDialog] = useState(false)
  const [idTobeDeletedOrRecovered, setIdTobeDeletedOrRecovered] = useState("")

  /**tracking user Activity */
  const { trackActivity } = useTrackUserActivity()
  useEffect(() => {
    if (isSuccessOnDelete) {
      trackActivity({
        activitySuccess: isSuccessOnDelete,
        actionType: variablesOnDelete?.isDeleteMode ? "Deleted" : "Recovered",
        itemId: variablesOnDelete?.Id || "",
      })
    }
  }, [variablesOnDelete, isSuccessOnDelete])


  return (
    <Fragment>
      {
        !!isLoadingOnReportData && fetchCommonComponentCMSLabel('Common.Waitloading')
      }
      <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
       {fetchClientSpecificCMSLabel("ClientSpecificReportsList.ClientSpecificReportsTitle")} 
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        m={1}
      >
        <Button
          variant="outlined"
          size="small"
          component={Link}
          to={clientSpecificReportCreatePath}
        >
          {fetchClientSpecificCMSLabel("ClientSpecificReportsList.UploadNewReports")} 
        </Button>
      </Stack>

      {/**incase successful call but empty response */}
      {!!isSuccess && !reportsData?.data?.length && (<NoTableDataComp />)}


      {!!isSuccess && !!reportsData?.data?.length && (

        <MaterialReactTable
          columns={columns}
          data={reportsData?.data || []}
          layoutMode="grid"
          enableGlobalFilter={false}
          enableDensityToggle={false}

          initialState={{
            density: "compact",
            showColumnFilters: true,
          }}
          state={{
            isLoading: isLoadingOnReportData,
            columnPinning: {
              left: ["mrt-row-actions", "Title"],
            },
          }}
          displayColumnDefOptions={{
            'mrt-row-actions': {
              header: fetchClientSpecificCMSLabel('ClientSpecificReportsList.Actions'), //change header text
              size: 100, //make actions column wider
            },
          }}
          muiTablePaginationProps={{
            labelRowsPerPage:fetchCommonComponentCMSLabel('Common.Rowsperpage')
          }}
          enableStickyHeader
          enableRowActions
          enablePinning
          renderToolbarInternalActions={({ table }) => (
            <>
              {canExportXls && (
                <ExportButton
                  table={table}
                  tooltipTitle={fetchClientSpecificCMSLabel("ClientSpecificReportsList.ExportToXLS")}
                  trackActivity={trackActivityMutate}
                  userId={userId}
                  exportColumns={exportColumns}
                  fileName={exportFileName}
                  worksheetName={exportWorksheetName}
                />
              )}
              <MRTToggleFiltersButton table={table} />
              <MRTShowHideColumnsButton table={table} />
              <MRTFullScreenToggleButton table={table} />
            </>
          )}

          renderRowActions={({ row, table }) => {
            const Id = row.original?.Id;
            const Status = row.original?.Status;
            return (
              <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '8px' }}>
                <IconButton
                  onClick={() => {
                    navigate(clientSpecificReportEditPath({ id: Id }));
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  onClick={() => {
                    setIdTobeDeletedOrRecovered({
                      id: Id,
                      status: Status
                    })
                    setOpenMpgConfirmDialog(true)
                  }}
                >
                  {Status === "Active" ? (
                    <Tooltip title="Delete">
                      <DeleteIcon />
                    </Tooltip>

                  ) : (
                    <Tooltip title="Recover">
                      <ReplayIcon />
                    </Tooltip>

                  )}
                </IconButton>
              </Box>
            )
          }}
        />
      )}

      {/** confirm dialog on delete action */}
      <MpgConfirmDialog
        title={`Confirm your action.`}
        content={`Are you sure you want to ${idTobeDeletedOrRecovered?.status === "Active" ? 'delete' : 'recover'} this entry.`}
        open={openMpgConfirmDialog}
        setOpen={setOpenMpgConfirmDialog}
        extraLogicHandlerOnAgree={() => {
          const id = idTobeDeletedOrRecovered?.id
          const status = idTobeDeletedOrRecovered?.status
          if (status === "Active") {
            deleteReportResourceById({ isDeleteMode: true, Id: id });
          } else {
            deleteReportResourceById({ isDeleteMode: false, Id: id });
          }
          setIdTobeDeletedOrRecovered("")
        }}
      />

      <MpgErrorDialog
        open={!!isError}
      />
    </Fragment>
  );
};

export default ViewClientSpecificReport;
